/*
 * Copyright(c) 1995 - 2020 T-Systems Multimedia Solutions GmbH
 * Riesaer Str. 5, 01129 Dresden
 * All rights reserved.
 */

import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Directive, ElementRef, HostBinding, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';
import { TooltipContentComponent } from './tooltip-content.component';

const TOOLTIP_POSITION: ConnectedPosition[] = [
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom' },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
];

/**
 * Opens a tooltip message above the host element on mouse hover or touch event.
 *
 * <pre>
 *     <common-form-button
 *           commonTooltip="deactivated"
 *           [commonTooltipDisabled]="disabled"
 *           commonTooltipIcon></common-form-button>
 * </pre>
 */
@Directive({
    selector: '[commonTooltip]'
})
export class TooltipDirective implements OnInit, OnDestroy {

    /**
     * The given message will be shown.
     */
    @Input('commonTooltip') message: string | null = '';

    /**
     * Deactivates the directive if true.
     * "False" by default.
     */
    @Input('commonTooltipDisabled') set disabled(val: boolean) {
        this.disabled$.next(val);
    }

    /**
     * Sets a horizontal offset for the tooltip.
     */
    @Input('commonTooltipOffsetX') offsetX;

    /**
     * Sets a vertical offset for the tooltip.
     */
    @Input('commonTooltipOffsetY') offsetY;

    /**
     * Adds a .focused css class when the message is shown.
     */
    @HostBinding('class.focused') focused = false;

    disabled$ = new BehaviorSubject(false)

    private isHover$ = new Subject<boolean>();

    private hover$ = this.disabled$.pipe(
        filter(disabled => !disabled),
        switchMap(() => this.isHover$),
        filter(isHover => isHover)
    );

    private hoverEnds$ = this.isHover$.pipe(
        filter(isHover => isHover),
        switchMap(() => this.isHover$.pipe(
            filter(isHover => !isHover),
            take(1)
        ))
    );

    constructor(
        private overlay: Overlay,
        private elementRef: ElementRef
    ) {
    }

    ngOnInit(): void {
        const portal: ComponentPortal<TooltipContentComponent> = new ComponentPortal(TooltipContentComponent);
        const overlayRef: OverlayRef = this.createOverlayRef();

        this.hover$.subscribe(_ => this.attach(portal, overlayRef));
        this.hoverEnds$.subscribe(_ => overlayRef.detach());
        this.isHover$.subscribe(isHover => this.focused = isHover);
    }

    ngOnDestroy(): void {
        this.isHover$.complete();
    }

    @HostListener('mouseenter')
    @HostListener('touchstart')
    onHover(): void {
        this.isHover$.next(true);
    }

    @HostListener('mouseleave')
    @HostListener('touchend')
    onHoverEnd(): void {
        this.isHover$.next(false);
    }

    private createOverlayRef(): OverlayRef {
        const positionStrategy = this.overlay.position()
            .flexibleConnectedTo(this.elementRef)
            .withViewportMargin(10)
            .withFlexibleDimensions(false)
            .withDefaultOffsetY(this.offsetY)
            .withDefaultOffsetX(this.offsetX)
            .withPositions(TOOLTIP_POSITION);
        return this.overlay.create({
            direction: 'ltr',
            positionStrategy,
            scrollStrategy: this.overlay.scrollStrategies.close()
        });
    }

    private attach(portal: ComponentPortal<TooltipContentComponent>,
                   overlayRef: OverlayRef): void {
        if (!this.message) {
            return;
        }
        if (overlayRef.hasAttached()) {
            return;
        }
        const instance = overlayRef.attach(portal).instance;
        instance.message = this.message;
    }
}
